import {useState, useEffect, useMemo, useCallback} from 'react';
import { Trash } from "react-bootstrap-icons";
import moment from "moment";
import axios from "axios";
import useGet from "../../components/hooks/useGet";
import { Alert, Button } from "react-bootstrap";
import Loading from "../../components/Loading";
import { Table } from "../../components/layout";
import { standardDateTimeFormat } from "../../utils/momentHelpers";
import OsIcon from "../../components/OSIcon";
import {ColumnDef} from "@tanstack/react-table";

interface LoginToken {
    id: number;
    name: string;
    last_used_at: string;
}

/** Displays a list of the user's active login sessions with the option to revoke individual tokens. */
export default function LoggedInDevices() {
    const { data, isLoading, error, reload } = useGet<LoginToken[]>({
        endpoint: '/tokens'
    });
    const [deleteError, setDeleteError] = useState('');
    const [isDeletingIds, setDeletingIds] = useState<Record<number, boolean>>({});

    useEffect(() => {
        // Clear the error after refreshing the login tokens list
        setDeleteError('');
    }, [isLoading]);

    const invalidateLoginToken = useCallback((id: number) => {
        setDeleteError('');
        if (!isDeletingIds.hasOwnProperty(id)) {
            isDeletingIds[id] = true;
            setDeletingIds(isDeletingIds);
            axios.delete('/tokens/' + id)
                .then(() => reload())
                .catch(e => {
                    setDeleteError(e.message);
                })
                .finally(() => {
                    setDeletingIds((oldDeleting) => {
                        delete oldDeleting[id];
                        return oldDeleting;
                    });
                });
        }
    }, [isDeletingIds, reload]);

    const columns = useMemo(() => {
        const innerColumns: ColumnDef<LoginToken>[] = [
            {
                header: 'Device',
                accessorKey: 'name',
                cell: ({ row }) => {
                    const value = row.original.name;
                    return (
                      <div className="d-flex align-items-center">
                          <OsIcon name={value} />
                          &nbsp;
                          <span>
                            <div className="text-truncate" style={{ maxWidth: '300px' }}>{value}</div>
                        </span>
                      </div>
                    );
                },
            },
            {
                header: 'Last used',
                accessorKey: 'last_used_at',
                cell: ({ row }) => moment(row.original.last_used_at).format(standardDateTimeFormat),
            },
            {
                header: '',
                accessorKey: 'id',
                cell: ({ row }) => {
                    const value = row.original.id;
                    return (
                      <div className="text-right">
                          {isDeletingIds[value]
                            ? <Loading size="sm" />
                            : (
                              <Button
                                size="sm"
                                className="py-0 border-0"
                                variant="outline-danger"
                                onClick={() => invalidateLoginToken(value)}
                              >
                                  <Trash />
                              </Button>
                            )}
                      </div>
                    );
                },
            }
        ];
        return innerColumns;
    }, [invalidateLoginToken, isDeletingIds]);

    return (
        <>
            {error && typeof error === 'string' && <Alert variant="danger">{error}</Alert>}
            {deleteError && typeof deleteError === 'string' && <Alert variant="danger">{deleteError}</Alert>}
            {isLoading && <Loading />}
            {Array.isArray(data) && (
                data.length === 0
                    ? <p className="neutral-600 fst-italic">No logins</p>
                    : <Table memoisedColumns={columns} memoisedData={data} />
            )}
        </>
    );
}
